import React, { useCallback, useMemo } from 'react';

import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { DeviceSharingRequest } from 'src/types/devices';
import { OperationStatus } from 'src/types';
import ApiError from 'src/types/error';

import { getExpiresAt } from 'src/utils/format-access-remaining';

import AccessRemainingCell from '../shared-table-layout/access-remaining-cell';
import ActionMenu from './action-menu';
import DeviceImageCell from '../shared-table-layout/device-image-cell';
import SharedTableLayout from '../shared-table-layout';
import SharingGuidelinesModal from '../sharing-guidelines-modal';
import { useSharedTableState } from 'src/hooks/useSharedTableState';
import { useSharedTableColumns } from 'src/hooks/useSharedTableColumns';
import { useSharedTableSorting } from 'src/hooks/useSharedTableSorting';

const EXTRA_COLUMNS = [
  { id: 'thirdPartyEmail', localeKeySuffix: 'columns.third-party-email' }
];

const SHARED_DEVICES_SORTABLE_COLUMNS = [
  'deviceName',
  'serialNumber',
  'thirdPartyEmail',
  'accessRemaining'
];

export type SharedDevicesTableProps = {
  devices: DeviceSharingRequest[] | null;
  status: OperationStatus;
  error: ApiError | null;
  onRetry: () => void;
};

const SharedDevicesTable: React.FC<SharedDevicesTableProps> = (
  props: SharedDevicesTableProps
) => {
  const { devices, status, error, onRetry } = props;
  const { translate } = useDependencyManagerContext();
  const { isLoading, isErrored, noDeviceName, unknownDeviceName } =
    useSharedTableState(status);

  const { sortedDevices, sortBy, handleSort, freezeAtOrder } =
    useSharedTableSorting(devices, noDeviceName);

  const columns = useSharedTableColumns(
    'shared-devices-table',
    EXTRA_COLUMNS,
    SHARED_DEVICES_SORTABLE_COLUMNS
  );

  const handleExtend = useCallback(() => {
    freezeAtOrder(sortedDevices.map((d) => d.id));
  }, [freezeAtOrder, sortedDevices]);

  const tableData = useMemo(() => {
    if (!sortedDevices) return [];
    return sortedDevices.map((sharingRequest) => ({
      id: sharingRequest.id,
      deviceName: sharingRequest.device.device_family ?? noDeviceName,
      deviceImage: (
        <DeviceImageCell
          imageUrl={sharingRequest.device.device_image_url}
          imageAlt={translate(
            'components.shared-devices-table.device-image-alt',
            {
              deviceFamily:
                sharingRequest.device.device_family ?? unknownDeviceName,
              serialNumber: sharingRequest.device.device_serial_nr
            }
          )}
          placeholderAriaLabel={translate(
            'components.shared-devices-table.device-image-not-available-aria-label',
            {
              deviceFamily:
                sharingRequest.device.device_family ?? unknownDeviceName
            }
          )}
        />
      ),
      serialNumber: sharingRequest.device.device_serial_nr,
      thirdPartyEmail: sharingRequest.user.email,
      accessRemaining: (
        <AccessRemainingCell
          expiresAt={getExpiresAt(sharingRequest.policies)}
        />
      ),
      action: (
        <ActionMenu
          sharingRequestId={sharingRequest.id}
          deviceFamily={sharingRequest.device.device_family ?? noDeviceName}
          serialNumber={sharingRequest.device.device_serial_nr}
          email={sharingRequest.user.email}
          onExtend={handleExtend}
        />
      )
    }));
  }, [sortedDevices, translate, noDeviceName, unknownDeviceName]);

  return (
    <SharedTableLayout
      testIdPrefix="shared-devices-table"
      title={translate('components.shared-devices-table.title')}
      headerAction={<SharingGuidelinesModal />}
      emptyMessage={translate('components.shared-devices-table.empty')}
      error={error}
      isErrored={isErrored}
      isLoading={isLoading}
      onRetry={onRetry}
      columns={columns}
      tableData={tableData}
      sortBy={sortBy}
      onSort={handleSort}
    />
  );
};

export default SharedDevicesTable;
